import { DEFAULT_PAGE_SIZE } from "@/modules/utils/paginated"
import { BoxStaffDetailedEntity, BoxStaffItemEntity } from "@modules/boxStaff/entities/boxStaffEntity"
import {
  BoxStaffActionRequest,
  type BoxStaffAddInput,
  type BoxStaffDetailedUpadteInput,
  BoxStaffStatus,
  StaffColumnToSort,
} from "@modules/boxStaff/gateway/boxStaffDto"
import { useServicesContext } from "@modules/core/services/services.context"
import useDebounce from "@modules/hooks/useDebounce"
import { ColumnSortingOrder } from "@modules/types/datatableTypes"
import { isValidSortOrder } from "@modules/ui/components/dataTables/datatableUtils"
import { usePersistedDatatable } from "@modules/utils/hooks/useDataTable"
import { useSetMultiParams } from "@modules/utils/navigation/useQueryParam"
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { useSearchParams } from "react-router"

export const useBoxStaffs = ({
  nameFilter,
  litsOfStatus,
  fetchArchived = false,
  page,
  pageSize,
  sortBy,
  sortOrder,
}: {
  nameFilter?: string
  litsOfStatus?: BoxStaffStatus[]
  fetchArchived?: boolean
  page?: number
  pageSize?: number
  sortBy?: string
  sortOrder?: string
}) => {
  const { boxStaffApi } = useServicesContext()

  const { data, isLoading } = useSuspenseQuery({
    queryKey: ["boxStaffs", { page, pageSize, nameFilter, sortBy, sortOrder, fetchArchived, litsOfStatus }],

    queryFn: async () => {
      if (sortBy && isValidStaffSortBy(sortBy) && sortOrder && isValidSortOrder(sortOrder)) {
        return await boxStaffApi.getBoxStaffs(
          page,
          pageSize,
          nameFilter,
          fetchArchived,
          litsOfStatus,
          sortBy,
          sortOrder,
        )
      }
      return await boxStaffApi.getBoxStaffs(0, 50, nameFilter, fetchArchived, litsOfStatus)
    },
  })

  return {
    data: data.content.map((boxStaff) => BoxStaffItemEntity.fromItemDto(boxStaff)) || [],
    totalElements: data.totalSize,
    isLoading,
    hasPreviousPage: data.page > 0,
    hasNextPage: data.page < data.totalPages - 1,
  }
}

export const usePersistedBoxStaffs = (litsOfStatus?: BoxStaffStatus[]) => {
  const [searchParams] = useSearchParams()
  const setListParams = useSetMultiParams()

  const sortBy = searchParams.get("sortBy") ?? undefined
  const sortOrder = searchParams.get("order") ?? undefined
  const nameFilter = searchParams.get("nameFilter") ?? undefined

  const debouncedSearchTerm = useDebounce(nameFilter, 300)

  const { page, setPage, size: pageSize, setSize: setPageSize } = usePersistedDatatable(DEFAULT_PAGE_SIZE)

  const { data, isLoading, hasPreviousPage, hasNextPage, totalElements } = useBoxStaffs({
    nameFilter: debouncedSearchTerm,
    page,
    pageSize,
    sortBy,
    sortOrder,
    litsOfStatus,
  })

  const handlePreviousPage = () => {
    if (hasPreviousPage) {
      setPage((prev) => prev - 1)
    }
  }

  const handleNextPage = () => {
    if (hasNextPage) {
      setPage((prev) => prev + 1)
    }
  }

  const setSorting = (field: StaffColumnToSort, order: ColumnSortingOrder) => {
    if (field === sortBy && order === sortOrder) {
      setListParams([
        { key: "sortBy", value: undefined },
        { key: "order", value: undefined },
      ])
    } else {
      setListParams([
        { key: "sortBy", value: field },
        { key: "order", value: order },
      ])
    }
  }

  return {
    data,
    totalElements,
    isLoading,
    pageSize,
    setPageSize,
    handlePreviousPage,
    handleNextPage,
    hasPreviousPage,
    hasNextPage,
    setSorting,
    sortBy,
    sortOrder,
    isSearching: !!nameFilter,
  }
}

export const useBoxStaff = (id: string) => {
  const { boxStaffApi } = useServicesContext()

  const { data, error } = useSuspenseQuery({
    queryKey: ["boxStaffs", id],
    queryFn: async () => {
      return await boxStaffApi.getBoxStaff(id)
    },
  })

  return {
    data: BoxStaffDetailedEntity.fromDetailedDto(data),
    error,
  }
}

export const useBoxStaffActions = (id: string) => {
  const { boxStaffApi } = useServicesContext()
  const queryClient = useQueryClient()

  // Update role
  const { mutateAsync: updateBoxStaff, isPending: isUpdateBoxStaff } = useMutation({
    mutationFn: async (input: BoxStaffDetailedUpadteInput) => {
      return await boxStaffApi.updateBoxStaff(id, input)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["boxStaffs"],
      })
    },
  })

  // Delete box staff
  const { mutateAsync: deleteBoxStaff, isPending: isDeleteBoxStaff } = useMutation({
    mutationFn: async () => {
      return await boxStaffApi.deleteBoxStaff(id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["boxStaffs"],
      })
      queryClient.invalidateQueries({
        queryKey: ["boxArchivedStaffs"],
      })
    },
  })

  const isPending = isUpdateBoxStaff || isDeleteBoxStaff

  return {
    deleteBoxStaff,
    updateBoxStaff,
    isPending,
  }
}

export const useAddBoxStaff = () => {
  const { boxStaffApi } = useServicesContext()
  const queryClient = useQueryClient()

  // Add box staff
  const { mutateAsync: addBoxStaff, isPending: isAddBoxStaff } = useMutation({
    mutationFn: async (input: BoxStaffAddInput) => {
      return await boxStaffApi.addBoxStaff(input)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["boxStaffs"],
      })
    },
  })

  return {
    addBoxStaff,
    isPending: isAddBoxStaff,
  }
}

export const useBoxStaffInvitation = () => {
  const { boxStaffApi } = useServicesContext()
  const queryClient = useQueryClient()

  // Accept box staff invitation
  const { mutateAsync: responseBoxStaffInvitation, isPending: isAcceptBoxStaffInvitation } = useMutation({
    mutationFn: async ({ boxId, action }: { boxId: string; action: BoxStaffActionRequest }) => {
      return await boxStaffApi.responseBoxStaffInvitation(boxId, action)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["boxStaffs"],
      })
    },
  })

  const isPending = isAcceptBoxStaffInvitation

  return {
    responseBoxStaffInvitation,
    isPending,
  }
}

export function isValidStaffSortBy(sortBy: string): sortBy is StaffColumnToSort {
  return Object.values(StaffColumnToSort).includes(sortBy as StaffColumnToSort)
}
